/**
 * One technique, opened: why it matters, how to do it, and the camera as
 * examiner.
 *
 * The order is deliberate. **Why** comes first, because "dry the surface" is a
 * rule you forget and "water has to boil off before browning can start" is a
 * reason you don't. Then the drawing, then the steps, then how you know it
 * worked. The check is last — it is the point, but it is useless before you
 * have read the rest.
 */

import { useState } from "react";

import { checkTechnique } from "../features/techniques/checkTechnique";
import type { Feedback } from "../features/techniques/checkTechnique";
import { recordAttempt } from "../features/techniques/practiceStorage";
import type { PracticeRecord, Verdict } from "../features/techniques/practiceStorage";
import type { Technique } from "../features/techniques/types";
import { isVisionConfigured } from "../features/vision/config";
import { todayIso } from "../lib/date/isoDate";
import { Icon } from "./Icon";
import { Sheet } from "./Sheet";
import { TechniqueDiagram } from "./TechniqueDiagram";
import { usePhotoCapture } from "./usePhotoCapture";

import "./TechniqueSheet.css";

const VERDICT_WORDS: Record<Verdict, string> = {
  good: "That looks right",
  close: "Close",
  off: "Not quite yet",
  unclear: "Couldn't tell from that photo",
};

interface TechniqueSheetProps {
  technique: Technique;
  practice?: PracticeRecord;
  /** Told after each attempt so the list behind can update its count. */
  onPractised: (verdict: Verdict) => void;
  onClose: () => void;
}

export function TechniqueSheet({
  technique,
  practice,
  onPractised,
  onClose,
}: TechniqueSheetProps) {
  const [status, setStatus] = useState<"idle" | "working" | "done" | "failed">("idle");
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function judge(photo: string) {
    if (!technique.check) return;
    setStatus("working");
    setFeedback(null);
    setProblem(null);

    const outcome = await checkTechnique(photo, technique.id, technique.check.looksFor);

    if (outcome.status === "ok") {
      setFeedback(outcome.feedback);
      setStatus("done");
      // Counted even when the verdict is "unclear": you did the thing, and a
      // log that only records successes is a log that discourages practice.
      recordAttempt(technique.id, outcome.feedback.verdict, todayIso());
      onPractised(outcome.feedback.verdict);
      return;
    }

    setStatus("failed");
    setProblem(
      outcome.status === "off"
        ? "Photo feedback needs a key set up — the technique still works without it."
        : outcome.message
    );
  }

  const { inputRef, isWorking, handleChange, open } = usePhotoCapture({
    onPhoto: (photo) => void judge(photo),
    onError: (message) => {
      setStatus("failed");
      setProblem(message);
    },
  });

  const busy = isWorking || status === "working";

  return (
    <Sheet title={technique.name} icon="recipes" onClose={onClose}>
      <div className="technique">
        <p className="technique__why">{technique.why}</p>

        <TechniqueDiagram id={technique.id} />

        <h3 className="technique__heading">Doing it</h3>
        <ol className="technique__steps">
          {technique.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>

        <h3 className="technique__heading">You'll know it worked when</h3>
        <ul className="technique__cues">
          {technique.cues.map((cue) => (
            <li key={cue}>
              <Icon name="check" className="technique__cue-icon" />
              {cue}
            </li>
          ))}
        </ul>

        <h3 className="technique__heading">What usually goes wrong</h3>
        <ul className="technique__mistakes">
          {technique.mistakes.map((mistake) => (
            <li key={mistake}>
              <Icon name="close" className="technique__mistake-icon" />
              {mistake}
            </li>
          ))}
        </ul>

        {technique.check ? (
          <section className="technique__check">
            <h3 className="technique__heading">Check yours</h3>
            <p className="technique__shoot">{technique.check.shoot}</p>

            <input
              ref={inputRef}
              className="sr-only"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleChange}
            />
            <button
              className="technique__shutter"
              type="button"
              onClick={open}
              disabled={busy}
            >
              <Icon
                name={busy ? "spinner" : "camera"}
                className="technique__shutter-icon"
                data-spinning={busy || undefined}
              />
              {busy ? "Looking…" : practice ? "Try again" : "Take a photo"}
            </button>

            {!isVisionConfigured() && (
              <p className="technique__note">
                Photo feedback is off — no key configured. Everything above still
                works.
              </p>
            )}

            {status === "failed" && problem && (
              <p className="technique__note" role="status">
                <Icon name="alert" className="technique__note-icon" data-tone="alert" />
                {problem}
              </p>
            )}

            {feedback && (
              <div className="technique__feedback" data-verdict={feedback.verdict}>
                <p className="technique__verdict">
                  <Icon
                    name={feedback.verdict === "good" ? "check" : "clock"}
                    className="technique__verdict-icon"
                  />
                  {VERDICT_WORDS[feedback.verdict]}
                </p>
                {feedback.observations.map((line) => (
                  <p className="technique__observation" key={line}>
                    {line}
                  </p>
                ))}
                {feedback.fix && <p className="technique__fix">Next time: {feedback.fix}</p>}
                {/* Said every time, because a beginner has no way to know how
                    much to trust a photo judged by a model. */}
                <p className="technique__caveat">
                  One photo, judged by a model — it can be wrong. Your eyes win.
                </p>
              </div>
            )}

            {practice && (
              <p className="technique__tally">
                Practised {practice.attempts} {practice.attempts === 1 ? "time" : "times"}
                {practice.best === "good" && " · best: looked right"}
              </p>
            )}
          </section>
        ) : (
          <p className="technique__note">
            {/* Honesty rather than a worse feature: a model asked to grade this
                from a photo would invent an answer, and a beginner couldn't tell. */}
            <Icon name="alert" className="technique__note-icon" />
            There's nothing useful to photograph for this one — it doesn't leave a
            mark a camera can judge. Read it, then taste as you go.
          </p>
        )}
      </div>
    </Sheet>
  );
}
